import { createStore } from 'zustand';
import qs, { ParsedQs } from 'qs';
import produce from 'immer';
import _ from 'lodash';
import { persist, PersistStorage } from 'zustand/middleware';

function getUrlFilters() {
  if (typeof window !== 'undefined') {
    return qs.parse(decodeURIComponent(window.location.search), {
      allowDots: true,
      comma: true,
      ignoreQueryPrefix: true,
    });
  } else {
    return {};
  }
}

const DEFAULT_LOCAL_STORAGE_FILTERS_KEY = '__DEFAULT_LS_FILTERS_KEY__';

type FilterKey = string;
type FilterValue = undefined | string | string[] | ParsedQs | ParsedQs[];

type Filters = {
  [key: FilterKey]: FilterValue;
};

export type FiltersConfigs = {
  [key: FilterKey]: {
    getInitialValueFromUrl?: boolean;
    getInitialValueFromLocalStorage?: boolean;
    setValueToUrl?: boolean;
    // TODO: Implement this two options in the next iteration
    // setValueToLocalStorage?: boolean;
  };
};

export type FiltersStoreState = {
  filters: Filters;
  subscribedUrlFiltersKeys: (keyof Filters)[];
  subscribeUrlFiltersKey: (key: keyof Filters) => void;
  unSubscribeUrlFiltersKey: (key: keyof Filters) => void;
  subscribedLocalStorageFiltersKeys: (keyof Filters)[];
  subscribeLocalStorageFiltersKey: (key: keyof Filters) => void;
  unSubscribeLocalStorageFiltersKey: (key: keyof Filters) => void;
  setFilter: (filterKey: FilterKey, value: FilterValue) => void;
  resetFilter: (filterKey: FilterKey) => void;
};

export type FiltersStore = ReturnType<typeof createFiltersStore>;

const persistentStorage = (
  initialFilters: Filters,
  filtersConfigs: FiltersConfigs,
): PersistStorage<{ filters: FiltersStoreState['filters'] }> => ({
  getItem: localStorageKey => {
    const urlFilters = getUrlFilters();
    // We only want to keep the filters that are in the initialFilters, but we want to keep other params in the URL like spoof.
    try {
      const localStorageFilters = JSON.parse(localStorage.getItem(localStorageKey) ?? '{}');
      const mergedFilters = Object.keys(initialFilters).reduce((acc, key) => {
        const urlFilterValue = urlFilters[key];
        const localStorageFilterValue = localStorageFilters[key];
        const filterConfig = filtersConfigs[key];
        if (filterConfig?.getInitialValueFromUrl !== false && urlFilterValue !== undefined) {
          acc[key] = urlFilterValue;
        } else if (
          filterConfig?.getInitialValueFromLocalStorage !== false &&
          localStorageFilterValue !== undefined
        ) {
          acc[key] = localStorageFilterValue;
        } else {
          acc[key] = initialFilters[key];
        }
        return acc;
      }, {} as FiltersStoreState['filters']);
      return {
        state: {
          filters: mergedFilters,
        },
      };
    } catch (e) {
      return null;
    }
  },
  // The debounce is to avoid setting the localStorage and the location.search too often
  setItem: _.debounce((localStorageKey, newState) => {
    const localStorageParams = _.pick(
      newState.state.filters,
      newState.state.subscribedLocalStorageFiltersKeys,
    );

    if (Object.keys(localStorageParams).length) {
      try {
        // We only want to save in the LS the filters that are in the initialFilters, to not add the garbage from the URL like spoof.
        const filters = JSON.stringify(localStorageParams ?? {});
        localStorage.setItem(localStorageKey, filters);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error: setItem', e);
      }
    }

    const urlParams = {
      ..._.omit(getUrlFilters(), Object.keys(initialFilters)),
      ..._.pick(
        newState.state.filters,
        newState.state.subscribedUrlFiltersKeys.filter(
          (key: string) => filtersConfigs?.[key]?.setValueToUrl !== false,
        ),
      ),
    };

    if (Object.keys(urlParams).length) {
      const searchUrl = qs.stringify(urlParams, {
        encode: false,
        allowDots: true,
        arrayFormat: 'comma',
        addQueryPrefix: true,
        filter: (prefix, value) => {
          if (value === undefined) {
            return;
          }
          if (_.isString(value) && value.length === 0) {
            return;
          }
          if (_.isArray(value) && value.length === 0) {
            return;
          }
          return value;
        },
      });

      if (typeof window !== 'undefined') {
        // We want to update the URL but keep any other params that are not filters like spoof.
        window.history.replaceState(null, '', searchUrl);
      }
    }
  }, 500),
  removeItem: localStorageKey => {
    try {
      localStorage.removeItem(localStorageKey);
      return;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error: removeItem', e);
      return;
    }
  },
});

export const createFiltersStore = (
  initialFilters: Filters = {},
  filtersConfigs: FiltersConfigs = {},
  localStorageKey = DEFAULT_LOCAL_STORAGE_FILTERS_KEY,
) => {
  return createStore<FiltersStoreState>()(
    persist(
      set => ({
        filters: initialFilters,
        setFilter: (filterKey, value) => {
          set(state =>
            produce(state, draftState => {
              _.set(draftState.filters, filterKey, value);
            }),
          );
        },
        resetFilter: filterKey => {
          set(state =>
            produce(state, draftState => {
              _.set(draftState.filters, filterKey, _.get(initialFilters, filterKey));
            }),
          );
        },
        subscribedUrlFiltersKeys: [] as (keyof Filters)[],
        subscribeUrlFiltersKey: key => {
          set(state =>
            produce(state, draftState => {
              if (!draftState.subscribedUrlFiltersKeys.includes(key)) {
                draftState.subscribedUrlFiltersKeys.push(key);
                draftState.subscribedUrlFiltersKeys.sort();
              }
            }),
          );
        },
        unSubscribeUrlFiltersKey: key => {
          set(state =>
            produce(state, draftState => {
              const index = draftState.subscribedUrlFiltersKeys.indexOf(key);
              if (index !== -1) {
                draftState.subscribedUrlFiltersKeys.splice(index, 1);
              }
            }),
          );
        },
        subscribedLocalStorageFiltersKeys: [] as (keyof Filters)[],
        subscribeLocalStorageFiltersKey: key => {
          set(state =>
            produce(state, draftState => {
              if (!draftState.subscribedLocalStorageFiltersKeys.includes(key)) {
                draftState.subscribedLocalStorageFiltersKeys.push(key);
              }
            }),
          );
        },
        unSubscribeLocalStorageFiltersKey: key => {
          set(state =>
            produce(state, draftState => {
              const index = draftState.subscribedLocalStorageFiltersKeys.indexOf(key);
              if (index !== -1) {
                draftState.subscribedLocalStorageFiltersKeys.splice(index, 1);
              }
            }),
          );
        },
      }),
      {
        name: localStorageKey,
        storage: persistentStorage(initialFilters, filtersConfigs),
      },
    ),
  );
};
